import { useCallback, useEffect, useRef, useState } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';
import maskSensitiveInfo from '@/integrations/supabase/utils';
import {
  calculateValidMoves,
  cloneBoard,
  getRealBoardPosition,
  getPlayerNumberForIdentity,
  getWinnerByPieces,
  hasPlayerSlot,
  hasTwoPlayers,
  isCurrentPlayerPiece as ownsCurrentPlayerPiece,
  parseBoard,
} from '@/lib/game';
import { ensureGuestName, getStoredGuestName } from '@/lib/player-identity';

type Game = Database['public']['Tables']['games']['Row'];
type GameUpdate = Database['public']['Tables']['games']['Update'];

export function useGame(roomId: string) {
  const session = useSession();
  const supabase = useSupabaseClient<Database>();
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const guestName = getStoredGuestName();

  const updateGameState = useCallback((newGameData: Game) => {
    setGame((prev) => {
      if (!prev) return newGameData;

      return new Date(newGameData.updated_at) > new Date(prev.updated_at)
        ? newGameData
        : prev;
    });
    setLoading(false);
  }, []);

  const fetchGame = useCallback(async (): Promise<Game | null> => {
    try {
      const { data, error } = await supabase
        .from('games')
        .select()
        .eq('room_id', roomId)
        .single();

      if (error) throw error;

      updateGameState(data);
      setError(null);
      setLoading(false);
      return data;
    } catch (err) {
      console.error('Erro ao carregar jogo:', err);
      setError('Erro ao carregar o jogo');
      setLoading(false);
      return null;
    }
  }, [roomId, supabase, updateGameState]);

  const sendSystemMessage = useCallback(async (gameId: string, message: string, eventType: string) => {
    try {
      const { error } = await supabase
        .from('chat_messages')
        .insert({
          game_id: gameId,
          message,
          is_system_message: true,
          system_event_type: eventType,
          created_at: new Date().toISOString(),
        });

      if (error) throw error;
    } catch (err) {
      console.error('Erro ao enviar mensagem do sistema:', err);
    }
  }, [supabase]);

  useEffect(() => {
    fetchGame();

    const channel = supabase
      .channel(`game:${roomId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'games',
          filter: `room_id=eq.${roomId}`,
        },
        (payload) => {
          console.log('Atualização do jogo recebida via postgres:', payload);
          if (payload.new) {
            updateGameState(payload.new as Game);
          }
        }
      )
      .on('broadcast', { event: 'game_update' }, ({ payload }) => {
        console.log('Atualização do jogo recebida via broadcast:', payload);

        const { lastMove: _lastMove, ...gameState } = payload as Game & { lastMove?: unknown };
        updateGameState(gameState);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('Inscrito no canal do jogo:', roomId);
        }
      });

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [roomId, fetchGame, supabase, updateGameState]);

  const joinGame = async () => {
    try {
      const latestGame = await fetchGame();
      const activeGame = latestGame ?? game;

      if (!activeGame) {
        console.error('Jogo não encontrado após refetch');
        return;
      }

      console.log('Estado atual do jogo:', {
        game_id: activeGame.id,
        room_id: activeGame.room_id,
        player1_id: activeGame.player1_id,
        player2_id: activeGame.player2_id,
        current_user: session?.user?.id || 'guest',
      });

      const isGuest = !session?.user;
      const guestNameFromStorage = isGuest ? ensureGuestName() : null;

      if (session?.user) {
        if (activeGame.player1_id === session.user.id) {
          console.log('Usuário já é o jogador 1');
          return;
        }

        if (activeGame.player2_id === session.user.id) {
          console.log('Usuário já é o jogador 2');
          return;
        }
      } else {
        if (activeGame.player1_name === guestNameFromStorage) {
          console.log('Convidado já é o jogador 1');
          return;
        }

        if (activeGame.player2_name === guestNameFromStorage) {
          console.log('Convidado já é o jogador 2');
          return;
        }
      }

      let updates: GameUpdate | null = null;
      let joinedAsPlayer1 = false;
      let joinedPlayerName = 'Anônimo';
      const player1Occupied = hasPlayerSlot(activeGame.player1_id, activeGame.player1_name);
      const player2Occupied = hasPlayerSlot(activeGame.player2_id, activeGame.player2_name);

      if (!player1Occupied) {
        joinedAsPlayer1 = true;

        if (session?.user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('display_name')
            .eq('id', session.user.id)
            .single();

          const playerName = profile?.display_name || session.user.email || 'Anônimo';
          joinedPlayerName = playerName;
          updates = {
            player1_id: session.user.id,
            player1_name: playerName,
            status: player2Occupied ? 'playing' : 'waiting',
            updated_at: new Date().toISOString(),
          };

        } else {
          const playerName = guestNameFromStorage || 'Anônimo';
          joinedPlayerName = playerName;
          updates = {
            player1_id: null,
            player1_name: playerName,
            status: player2Occupied ? 'playing' : 'waiting',
            updated_at: new Date().toISOString(),
          };

        }

        console.log('Tentando entrar como jogador 1');
      } else if (
        !player2Occupied &&
        player1Occupied
      ) {
        if (session?.user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('display_name')
            .eq('id', session.user.id)
            .single();

          const playerName = profile?.display_name || session.user.email || 'Anônimo';
          joinedPlayerName = playerName;
          updates = {
            player2_id: session.user.id,
            player2_name: playerName,
            status: 'playing',
            updated_at: new Date().toISOString(),
          };

        } else {
          const playerName = guestNameFromStorage || 'Anônimo';
          joinedPlayerName = playerName;
          updates = {
            player2_id: null,
            player2_name: playerName,
            status: 'playing',
            updated_at: new Date().toISOString(),
          };

        }

        console.log('Tentando entrar como jogador 2');
      } else {
        console.log('Jogo já está cheio');
        return;
      }

      console.log('Dados para atualização:', {
        updates,
        room_id: roomId,
      });

      let updateQuery = supabase
        .from('games')
        .update(updates)
        .eq('id', activeGame.id);

      updateQuery = joinedAsPlayer1
        ? updateQuery.is('player1_id', null).is('player1_name', null)
        : updateQuery.is('player2_id', null).is('player2_name', null);

      const { data: updatedGame, error: updateError } = await updateQuery
        .select('*')
        .maybeSingle();

      if (updateError) {
        console.error('Erro ao atualizar jogo:', updateError);
        toast.error('Erro ao entrar no jogo. Por favor, tente novamente.');
        throw updateError;
      }

      if (!updatedGame) {
        await fetchGame();
        toast.error('Essa vaga acabou de ser ocupada. Atualizei a sala para você.');
        return;
      }

      await sendSystemMessage(activeGame.id, `👋 ${joinedPlayerName} entrou`, 'player_joined');

      console.log('Jogador adicionado com sucesso:', updatedGame);
      toast.success('Você entrou no jogo!');
      updateGameState(updatedGame);

      const channel = supabase.channel(`game_room_${activeGame.id}`);
      await channel.send({
        type: 'broadcast',
        event: 'player_joined',
        payload: {
          playerId: session?.user?.id || null,
          playerName: joinedPlayerName,
          isPlayer1: joinedAsPlayer1,
        },
      });
    } catch (error) {
      console.error('Erro ao entrar no jogo:', maskSensitiveInfo(error));
      toast.error('Erro ao entrar no jogo');
    }
  };

  const makeMove = async (
    fromRow: number,
    fromCol: number,
    toRow: number,
    toCol: number,
    isCapture: boolean,
    isPromotion: boolean,
    capturedRow?: number,
    capturedCol?: number,
    keepTurn: boolean = false
  ) => {
    if (!game) return;

    try {
      const newBoard = cloneBoard(parseBoard(game.board));
      const piece = newBoard[fromRow][fromCol];
      newBoard[toRow][toCol] = isPromotion ? (game.current_player === 1 ? 3 : 4) : piece;
      newBoard[fromRow][fromCol] = 0;

      let shouldChangeTurn = !keepTurn;

      if (isCapture && capturedRow !== undefined && capturedCol !== undefined) {
        newBoard[capturedRow][capturedCol] = 0;

        const hasMoreCaptures = calculateValidMoves(newBoard, toRow, toCol, {
          currentPlayer: game.current_player,
          onlyCaptures: true,
        }).some((move) => move.isCapture);

        if (hasMoreCaptures) {
          shouldChangeTurn = false;
          toast.info('Você deve continuar capturando com a mesma peça!');
        } else {
          shouldChangeTurn = true;
        }
      }

      const winnerPlayer = getWinnerByPieces(newBoard);
      const winnerId = winnerPlayer === 1
        ? game.player1_id || null
        : winnerPlayer === 2
          ? game.player2_id || null
          : null;
      const newStatus = winnerPlayer ? 'finished' : game.status;

      const newGameState: Game = {
        ...game,
        board: newBoard,
        current_player: shouldChangeTurn ? (game.current_player === 1 ? 2 : 1) : game.current_player,
        winner_id: winnerId,
        status: newStatus,
        updated_at: new Date().toISOString(),
      };

      updateGameState(newGameState);

      try {
        const { error: updateError } = await supabase
          .from('games')
          .update({
            board: newBoard,
            current_player: newGameState.current_player,
            winner_id: winnerId,
            status: newStatus,
            updated_at: newGameState.updated_at,
          })
          .eq('id', game.id);

        if (updateError) throw updateError;

        const moveData = {
          game_id: game.id,
          player_id: session?.user?.id || null,
          player_name: session?.user ? null : getStoredGuestName(),
          from_row: fromRow,
          from_col: fromCol,
          to_row: toRow,
          to_col: toCol,
          from_position: getRealBoardPosition(fromRow, fromCol),
          to_position: getRealBoardPosition(toRow, toCol),
          is_capture: isCapture,
          is_promotion: isPromotion,
          current_player: game.current_player,
          created_at: new Date().toISOString(),
        };

        const { error: moveError } = await supabase
          .from('moves')
          .insert(moveData);

        if (moveError) console.error('Erro ao salvar movimento:', moveError);

        if (channelRef.current) {
          await channelRef.current.send({
            type: 'broadcast',
            event: 'game_update',
            payload: newGameState,
          });
        }
      } catch (err) {
        console.error('Erro ao realizar movimento:', err);
        toast.error('Erro ao realizar movimento');
        await fetchGame();
      }
    } catch (err) {
      console.error('Erro ao realizar movimento:', err);
      toast.error('Erro ao realizar movimento');
      await fetchGame();
    }
  };

  const isCurrentPlayerPiece = (row: number, col: number): boolean => {
    if (!game) return false;

    return ownsCurrentPlayerPiece(parseBoard(game.board), row, col, game, {
      userId: session?.user?.id,
      guestName: getStoredGuestName(),
    });
  };

  const currentPlayerNumber = game
    ? getPlayerNumberForIdentity(game, {
        userId: session?.user?.id,
        guestName: session?.user ? null : guestName,
      })
    : null;
  const isPlayer1 = currentPlayerNumber === 1;
  const isPlayer2 = currentPlayerNumber === 2;
  const hasActivePlayers = hasTwoPlayers(game);
  const isGameActive = game?.winner_id === null && game?.status !== 'finished';

  return {
    game,
    loading,
    error,
    joinGame,
    makeMove,
    isPlayer1,
    isPlayer2,
    canPlay: isGameActive && hasActivePlayers,
    refetch: fetchGame,
    isCurrentPlayerPiece,
    canJoinGame: !!(
      game &&
      !currentPlayerNumber &&
      isGameActive &&
      !hasActivePlayers
    ),
  };
}
